"use client";

import type {ListBoxProps} from "react-aria-components";
import {
	ListBox,
	ListBoxItem,
	ListBoxItemProps,
	Popover,
	PopoverProps,
} from "react-aria-components";
import clsMerge from "../../util/clsMerge";

type SelectionListClasses = {
	root?: string;
	selection?: string;
};

interface SelectionListProps<T extends object>
	extends Omit<ListBoxProps<T>, "children"> {
	classes?: SelectionListClasses;
	children?: React.ReactNode | ((item: T) => React.ReactNode);
	popoverProps?: PopoverProps;
}

// https://react-spectrum.adobe.com/react-spectrum/ListBox.html#listbox
function SelectionList<T extends object>({
	classes,
	children,
	popoverProps,
	...props
}: SelectionListProps<T>) {
	return (
		<Popover className={classes?.root} {...popoverProps}>
			<ListBox
				className={clsMerge(
					"max-h-[inherit] overflow-y-auto overflow-x-hidden rounded-md border border-solid border-gray-100 bg-white",
					classes?.selection
				)}
				{...props}
			>
				{children}
			</ListBox>
		</Popover>
	);
}

function SelectionItem<T extends object>({
	children,
	className,
	...props
}: ListBoxItemProps<T>) {
	return (
		<ListBoxItem
			className={({isFocused, isHovered, isDisabled}) => {
				return clsMerge(
					"px-4 py-1.5",
					isHovered && "bg-pale-light-cyan",
					isDisabled && "italic text-gray-500",
					isFocused && "bg-pale-light-cyan",
					className
				);
			}}
			{...props}
		>
			{children}
		</ListBoxItem>
	);
}

export type {SelectionListClasses};

export {SelectionItem, SelectionList};
